import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";
import styled from "styled-components";

import { Alert, AlertSuccess } from "components/common/Alert";
import { Input } from "components/common/Input";
import { ValidateButton } from "components/common/ValidateButton";
import { loginFormScenario } from "components/helper/callApi";

import { HTTPResponseError } from "services/errors";

import { VoteStatus } from "types/voteEnum";
import { Scenario } from "types/scenarioType";

type Props = {
    codeOnline: string;
    scenario: Scenario;
    email: string;
    password: string;
    phoneNumber: string;
    onSubmitSuccess: (token: string) => Promise<void>;
    requestVerificationCode: (email: string, password: string) => Promise<void>;
    disabled?: boolean;
};

const SMSVerificationForm = ({
    codeOnline,
    scenario,
    email,
    password,
    phoneNumber,
    requestVerificationCode,
    onSubmitSuccess,
    disabled,
}: Props) => {
    const { t } = useTranslation();

    const [status, setStatus] = useState(VoteStatus.Idle);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const [newCodeStatus, setNewCodeStatus] = useState(VoteStatus.Idle);
    const [newCodeErrorMessage, setNewCodeErrorMessage] = useState<
        string | null
    >(null);

    const verificationCodeRef = useRef<HTMLInputElement>(null);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!verificationCodeRef.current) {
            return;
        }

        const verificationCode = verificationCodeRef.current.value;

        let token: string | null = null;

        try {
            token = await loginFormScenario(codeOnline, scenario, {
                email: email,
                password: password,
                verificationCode: verificationCode,
            });
        } catch (error) {
            if (!(error instanceof HTTPResponseError)) {
                setErrorMessage(`${t("errorUnknown")}`);
                setStatus(VoteStatus.Error);
                return;
            }

            const res = error.response;

            switch (res.status) {
                case 401:
                    setErrorMessage(`${t("errorCredentialsIncorrect")}`);
                    break;
                case 429:
                    setErrorMessage(`${t("errorTooManyRequest")}`);
                    break;
                default:
                    setErrorMessage(`${t("errorUnknown")}`);
            }

            setStatus(VoteStatus.Error);
            return;
        }

        try {
            await onSubmitSuccess(token ?? "");
        } catch (error) {
            if (!(error instanceof HTTPResponseError)) {
                setErrorMessage(`${t("errorUnknown")}`);
                setStatus(VoteStatus.Error);
                return;
            }

            const res = error.response;

            switch (res.status) {
                case 401:
                    enqueueSnackbar(`${t("errorCredentialsIncorrect")}`, {
                        variant: "error",
                        persist: true,
                    });
                    break;
                case 403:
                    enqueueSnackbar(`${t("error403")}`, {
                        variant: "error",
                        persist: true,
                    });
                    break;
                default:
                    setErrorMessage(`${t("errorUnknown")}`);
                    setStatus(VoteStatus.Error);
                    break;
            }
        }
    };

    const codeSentAlert =
        VoteStatus.Success === newCodeStatus ? (
            <AlertSuccess>{`${t("verificationCodeSent")}`}</AlertSuccess>
        ) : null;
    // An error, announced as such (RGAA 7.5.2), though styled as a success
    const newCodeErrorAlert = newCodeErrorMessage ? (
        <AlertSuccess role="alert">{newCodeErrorMessage}</AlertSuccess>
    ) : null;

    return (
        <>
            <FormWrapper>
                <form onSubmit={handleSubmit}>
                    <StyledParagraph>
                        {`${t("passwordEmailSmsDescription", {
                            phoneNumber,
                        })}`}
                    </StyledParagraph>
                    {errorMessage && <AlertError>{errorMessage}</AlertError>}
                    <div>
                        <StyledInput
                            ref={verificationCodeRef}
                            type="text"
                            placeholder={t("verificationCode")}
                            required
                            aria-label={t("verificationCode")}
                            disabled={disabled}
                        />
                    </div>
                    <div>
                        <ValidateStyledButton
                            disabled={
                                disabled ||
                                VoteStatus.PendingValidation === status
                            }
                        >
                            {`${t("validate")}`}
                        </ValidateStyledButton>
                    </div>
                </form>
            </FormWrapper>
            <WrapperResendCode>
                {/* Always in the page, so the message is announced (RGAA 7.5) */}
                <div role="status">{codeSentAlert}</div>
                {newCodeErrorAlert}
                <p>{`${t("noReceivedCode")}`}</p>
                <StyledSendCodeText
                    onClick={async () => {
                        setNewCodeStatus(VoteStatus.PendingValidation);
                        setNewCodeErrorMessage(null);

                        try {
                            await requestVerificationCode(email, password);
                            setNewCodeStatus(VoteStatus.Success);
                        } catch (errorMessageParam: any) {
                            setNewCodeErrorMessage(errorMessageParam);
                            setNewCodeStatus(VoteStatus.Error);
                        }
                    }}
                >{`${t("resendSMSCode")}`}</StyledSendCodeText>
            </WrapperResendCode>
        </>
    );
};

const StyledParagraph = styled.p`
    font-size: 15px;
    line-height: 1.5rem;
    margin: 0;
    padding-bottom: 1rem;
`;

const FormWrapper = styled.div`
    padding: 1rem 1.5rem 0 1.5rem;
`;

const AlertError = styled(Alert)``;

const WrapperResendCode = styled.div`
    font-size: 12px;
    padding: 1rem 1.5rem 1rem 1.5rem;
    > p {
        margin: 0;
    }
`;

const StyledSendCodeText = styled.div`
    text-decoration: underline;
    cursor: pointer;
`;

const StyledInput = styled(Input)``;

const ValidateStyledButton = styled(ValidateButton)``;

export default SMSVerificationForm;
